// ============================================
// Channel Management Components
// Create, Browse, Settings, and Member Management
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from './ChatProvider';
import { API_URL, getAnyValidToken } from '../../utils/authUtils';

// ============================================
// CREATE CHANNEL MODAL
// ============================================
export const CreateChannelModal = ({ isOpen, onClose, onCreated }) => {
  const { createChannel, currentUser } = useChat();
  const isAdmin = currentUser?.type === 'admin';

  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    visibility: 'public',
    channel_type: 'general',
    icon: '',
    target_audience: 'all'  // Only used by admins
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.display_name.trim()) {
      setError('Channel name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Only include target_audience for admins
      const channelData = isAdmin
        ? formData
        : { ...formData, target_audience: undefined };

      const channel = await createChannel(channelData);
      if (channel) {
        onCreated?.(channel);
        onClose();
        setFormData({
          display_name: '',
          description: '',
          visibility: 'public',
          channel_type: 'general',
          icon: '',
          target_audience: 'all'
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to create channel');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Create Channel</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Channel Name *
            </label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., General Discussion"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="What's this channel about?"
              rows={3}
              maxLength={500}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon (emoji)
            </label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value.slice(0, 2) }))}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-xl"
              placeholder="💬"
              maxLength={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visibility
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={formData.visibility === 'public'}
                  onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Public</span>
                  <span className="text-gray-500 block text-xs">Anyone can join</span>
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={formData.visibility === 'private'}
                  onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Private</span>
                  <span className="text-gray-500 block text-xs">Invite only</span>
                </span>
              </label>
            </div>
          </div>

          {/* Target Audience - Admin only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Audience
                <span className="text-xs text-purple-600 ml-2">(Admin only)</span>
              </label>
              <select
                value={formData.target_audience}
                onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Users</option>
                <option value="students_only">Students Only</option>
                <option value="alumni_only">Alumni Only</option>
                <option value="recruiters_only">Recruiters Only</option>
                <option value="students_and_alumni">Students & Alumni</option>
                <option value="students_and_recruiters">Students & Recruiters</option>
                <option value="alumni_and_recruiters">Alumni & Recruiters</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Non-admin users can only create channels for their own user type.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span>{isSubmitting ? 'Creating...' : 'Create Channel'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ============================================
// BROWSE CHANNELS MODAL
// ============================================
export const BrowseChannelsModal = ({ isOpen, onClose, onJoined }) => {
  const { browseChannels, joinChannel } = useChat();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    const data = await browseChannels();
    setChannels(data);
    setLoading(false);
  }, [browseChannels]);

  useEffect(() => {
    if (isOpen) {
      loadChannels();
    }
  }, [isOpen, loadChannels]);

  const handleJoin = async (channelId) => {
    setJoiningId(channelId);
    const success = await joinChannel(channelId);
    if (success) {
      setChannels(prev => prev.filter(ch => ch.id !== channelId));
      onJoined?.();
    }
    setJoiningId(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Browse Channels</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : channels.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p>No channels available to join</p>
              <p className="text-sm text-gray-400">You've joined all public channels!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {channels.map(channel => (
                <div
                  key={channel.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{channel.icon || '#'}</span>
                      <span className="font-medium text-gray-900 truncate">{channel.display_name}</span>
                    </div>
                    {channel.description && (
                      <p className="text-sm text-gray-500 truncate mt-0.5">{channel.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {channel.member_count || 0} members
                    </p>
                  </div>
                  <button
                    onClick={() => handleJoin(channel.id)}
                    disabled={joiningId === channel.id}
                    className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {joiningId === channel.id ? 'Joining...' : 'Join'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================
// CHANNEL SETTINGS MODAL
// ============================================
export const ChannelSettingsModal = ({ isOpen, onClose, channel }) => {
  const {
    updateChannel,
    archiveChannel,
    getChannelMembers,
    addChannelMembers,
    removeChannelMember,
    updateMemberRole,
    searchUsers,
    currentUser,
    previewAudienceChange
  } = useChat();

  const isPlatformAdmin = currentUser?.type === 'admin';

  const [activeTab, setActiveTab] = useState('general');
  const [members, setMembers] = useState([]);
  const [yourRole, setYourRole] = useState('member');
  const [loadingMembers, setLoadingMembers] = useState(false);

  // General tab state
  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    visibility: 'public',
    icon: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Audience change state (platform admin only)
  const [newAudience, setNewAudience] = useState('');
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [isPreviewingAudience, setIsPreviewingAudience] = useState(false);
  const [showAudienceConfirm, setShowAudienceConfirm] = useState(false);
  const [isChangingAudience, setIsChangingAudience] = useState(false);

  // Add member state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [addingMember, setAddingMember] = useState(null);

  // Archive state
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  // Mute/Ban state
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [muteReason, setMuteReason] = useState('');
  const [muteDuration, setMuteDuration] = useState('1hour');
  const [banReason, setBanReason] = useState('');
  const [isMuting, setIsMuting] = useState(false);
  const [isBanning, setIsBanning] = useState(false);
  const [isUnmuting, setIsUnmuting] = useState(null);
  const [isUnbanning, setIsUnbanning] = useState(null);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loadingBans, setLoadingBans] = useState(false);

  // Load channel data when opened
  useEffect(() => {
    if (isOpen && channel) {
      setFormData({
        display_name: channel.display_name || '',
        description: channel.description || '',
        visibility: channel.visibility || 'public',
        icon: channel.icon || ''
      });
      setNewAudience(channel.target_audience || 'all');
      setAudiencePreview(null);
      setShowAudienceConfirm(false);
      loadMembers();
    }
  }, [isOpen, channel]);

  const loadMembers = async () => {
    if (!channel) return;
    setLoadingMembers(true);
    const data = await getChannelMembers(channel.id);
    if (data) {
      setMembers(data.members || []);
      setYourRole(data.your_role || 'member');
    }
    setLoadingMembers(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await updateChannel(channel.id, formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    const results = await searchUsers(searchQuery);
    // Filter out users already in the channel
    const memberKeys = new Set(members.map(m => `${m.user_type}:${m.user_id}`));
    setSearchResults(results.filter(u => !memberKeys.has(`${u.user_type}:${u.user_id}`)));
    setIsSearching(false);
  };

  const handleAddMember = async (user) => {
    setAddingMember(`${user.user_type}:${user.user_id}`);
    try {
      await addChannelMembers(channel.id, [{ user_type: user.user_type, user_id: user.user_id }]);
      setSearchResults(prev => prev.filter(u => u.user_id !== user.user_id || u.user_type !== user.user_type));
      await loadMembers();
    } catch (err) {
      console.error('Failed to add member:', err);
    }
    setAddingMember(null);
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.display_name} from this channel?`)) return;
    try {
      await removeChannelMember(channel.id, member.user_type, member.user_id);
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRoleChange = async (member, newRole) => {
    if (newRole === 'owner') {
      if (!window.confirm(`Transfer ownership to ${member.display_name}? You will become an admin.`)) return;
    }
    try {
      await updateMemberRole(channel.id, member.user_type, member.user_id, newRole);
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveChannel(channel.id);
      onClose();
    } catch (err) {
      alert(err.message);
    }
    setIsArchiving(false);
    setShowArchiveConfirm(false);
  };

  // Mute/Ban handlers
  const openMuteModal = (member) => {
    setSelectedMember(member);
    setMuteReason('');
    setMuteDuration('1hour');
    setShowMuteModal(true);
  };

  const openBanModal = (member) => {
    setSelectedMember(member);
    setBanReason('');
    setShowBanModal(true);
  };

  // Convert duration string to minutes
  const getDurationMinutes = (duration) => {
    const durationMap = {
      '1hour': 60,
      '6hours': 360,
      '1day': 1440,
      '3days': 4320,
      '7days': 10080,
      '30days': 43200,
      'permanent': null
    };
    return durationMap[duration] ?? null;
  };

  const handleMute = async () => {
    if (!selectedMember || !channel) return;
    setIsMuting(true);
    try {
      const token = getAnyValidToken();
      const durationMinutes = getDurationMinutes(muteDuration);
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channel.id}/members/${selectedMember.user_type}/${selectedMember.user_id}/mute`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            duration_minutes: durationMinutes,
            reason: muteReason || null
          })
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to mute user');
      }
      setShowMuteModal(false);
      setSelectedMember(null);
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
    setIsMuting(false);
  };

  const handleUnmute = async (member) => {
    if (!channel) return;
    setIsUnmuting(`${member.user_type}:${member.user_id}`);
    try {
      const token = getAnyValidToken();
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channel.id}/members/${member.user_type}/${member.user_id}/mute`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unmute user');
      }
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
    setIsUnmuting(null);
  };

  const handleBan = async () => {
    if (!selectedMember || !channel) return;
    if (!window.confirm(`Are you sure you want to ban ${selectedMember.display_name}? They will be removed from the channel and cannot rejoin.`)) return;
    setIsBanning(true);
    try {
      const token = getAnyValidToken();
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channel.id}/members/${selectedMember.user_type}/${selectedMember.user_id}/ban`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: banReason || null
          })
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to ban user');
      }
      setShowBanModal(false);
      setSelectedMember(null);
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
    setIsBanning(false);
  };

  const loadBannedUsers = async () => {
    if (!channel) return;
    setLoadingBans(true);
    try {
      const token = getAnyValidToken();
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channel.id}/bans`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setBannedUsers(data.bans || []);
      }
    } catch (err) {
      console.error('Failed to load banned users:', err);
    }
    setLoadingBans(false);
  };

  const handleUnban = async (ban) => {
    if (!channel) return;
    setIsUnbanning(`${ban.user_type}:${ban.user_id}`);
    try {
      const token = getAnyValidToken();
      const response = await fetch(
        `${API_URL}/api/chat/channels/${channel.id}/members/${ban.user_type}/${ban.user_id}/ban`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unban user');
      }
      await loadBannedUsers();
    } catch (err) {
      alert(err.message);
    }
    setIsUnbanning(null);
  };

  // Preview audience change impact (platform admin only)
  const handlePreviewAudienceChange = async () => {
    if (newAudience === channel.target_audience) {
      return;
    }
    setIsPreviewingAudience(true);
    try {
      const preview = await previewAudienceChange(channel.id, newAudience);
      setAudiencePreview(preview);
      if (preview.members_to_remove > 0) {
        setShowAudienceConfirm(true);
      } else {
        // No members affected, apply directly
        await handleConfirmAudienceChange();
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to preview audience change');
    }
    setIsPreviewingAudience(false);
  };

  // Confirm and apply audience change
  const handleConfirmAudienceChange = async () => {
    setIsChangingAudience(true);
    try {
      await updateChannel(channel.id, { target_audience: newAudience });
      setSaveSuccess(true);
      setShowAudienceConfirm(false);
      setAudiencePreview(null);
      await loadMembers(); // Reload members since some may have been removed
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to change audience');
    }
    setIsChangingAudience(false);
  };

  if (!isOpen || !channel) return null;

  const isOwner = yourRole === 'owner';
  const isAdmin = yourRole === 'admin' || isOwner;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <span className="text-xl">{channel.icon || '#'}</span>
            <h2 className="text-lg font-semibold text-gray-900">{channel.display_name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'members'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Members ({members.length})
          </button>
          {(isAdmin || isPlatformAdmin) && (
            <button
              onClick={() => {
                setActiveTab('banned');
                loadBannedUsers();
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'banned'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Banned
            </button>
          )}
          {isOwner && (
            <button
              onClick={() => setActiveTab('danger')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'danger'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Danger Zone
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-4">
              {saveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {saveError}
                </div>
              )}
              {saveSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  Changes saved successfully!
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  disabled={!isAdmin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!isAdmin}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value.slice(0, 2) }))}
                  disabled={!isAdmin}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-center text-xl"
                  maxLength={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibility {!isOwner && <span className="text-gray-400">(Owner only)</span>}
                </label>
                <div className="flex space-x-4">
                  <label className={`flex items-center space-x-2 ${isOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={formData.visibility === 'public'}
                      onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                      disabled={!isOwner}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Public</span>
                  </label>
                  <label className={`flex items-center space-x-2 ${isOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                    <input
                      type="radio"
                      name="visibility"
                      value="private"
                      checked={formData.visibility === 'private'}
                      onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value }))}
                      disabled={!isOwner}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Private</span>
                  </label>
                </div>
              </div>

              {/* Target Audience - Platform Admin only */}
              {isPlatformAdmin && (
                <div className="pt-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                    <span className="text-xs text-purple-600 ml-2">(Platform Admin only)</span>
                  </label>
                  <div className="flex items-center space-x-3">
                    <select
                      value={newAudience}
                      onChange={(e) => setNewAudience(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="all">All Users</option>
                      <option value="students_only">Students Only</option>
                      <option value="alumni_only">Alumni Only</option>
                      <option value="recruiters_only">Recruiters Only</option>
                      <option value="students_and_alumni">Students & Alumni</option>
                      <option value="students_and_recruiters">Students & Recruiters</option>
                      <option value="alumni_and_recruiters">Alumni & Recruiters</option>
                    </select>
                    <button
                      type="button"
                      onClick={handlePreviewAudienceChange}
                      disabled={newAudience === channel.target_audience || isPreviewingAudience}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPreviewingAudience ? 'Checking...' : 'Change'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Current: <span className="font-medium">{channel.target_audience || 'all'}</span>
                    {newAudience !== channel.target_audience && (
                      <span className="text-purple-600 ml-2">→ {newAudience}</span>
                    )}
                  </p>

                  {/* Audience Change Confirmation Modal */}
                  {showAudienceConfirm && audiencePreview && (
                    <div className="mt-4 p-4 border border-amber-300 bg-amber-50 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <h4 className="font-medium text-amber-800">
                            {audiencePreview.members_to_remove} member(s) will be removed
                          </h4>
                          <p className="text-sm text-amber-700 mt-1">
                            Changing the audience from "{audiencePreview.current_audience}" to "{audiencePreview.new_audience}" will remove the following members:
                          </p>
                          <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {audiencePreview.affected_members?.map((member, idx) => (
                              <li key={idx} className="text-sm text-amber-800 flex items-center space-x-2">
                                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                                <span>{member.display_name || `${member.user_type} #${member.user_id}`}</span>
                                <span className="text-xs text-amber-600">({member.user_type})</span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-4 flex space-x-3">
                            <button
                              onClick={handleConfirmAudienceChange}
                              disabled={isChangingAudience}
                              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                            >
                              {isChangingAudience ? 'Changing...' : 'Confirm Change'}
                            </button>
                            <button
                              onClick={() => {
                                setShowAudienceConfirm(false);
                                setAudiencePreview(null);
                                setNewAudience(channel.target_audience || 'all');
                              }}
                              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="pt-4">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="space-y-4">
              {/* Add Members (Admin/Owner only) */}
              {isAdmin && (
                <div className="pb-4 border-b">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Members</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search by name..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      onClick={handleSearch}
                      disabled={isSearching}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {isSearching ? '...' : 'Search'}
                    </button>
                  </div>

                  {searchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto">
                      {searchResults.map(user => (
                        <div key={`${user.user_type}:${user.user_id}`} className="flex items-center justify-between p-2">
                          <div>
                            <span className="font-medium">{user.display_name}</span>
                            <span className="text-xs text-gray-500 ml-2">{user.user_type}</span>
                          </div>
                          <button
                            onClick={() => handleAddMember(user)}
                            disabled={addingMember === `${user.user_type}:${user.user_id}`}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {addingMember === `${user.user_type}:${user.user_id}` ? '...' : 'Add'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Member List */}
              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map(member => {
                    const isCurrentUser = currentUser?.type === member.user_type && currentUser?.id === member.user_id;
                    const canManage = isOwner || (isAdmin && member.role === 'member');
                    const canChangeRole = isOwner && !isCurrentUser;

                    return (
                      <div key={`${member.user_type}:${member.user_id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                            {member.display_name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{member.display_name}</span>
                              {isCurrentUser && <span className="text-xs text-gray-500">(you)</span>}
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              member.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                              member.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {member.role}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {/* Muted indicator */}
                          {member.is_muted && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                              </svg>
                              <span>Muted</span>
                            </span>
                          )}
                          {canChangeRole && (
                            <select
                              value={member.role}
                              onChange={(e) => handleRoleChange(member, e.target.value)}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                            >
                              <option value="member">Member</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          )}
                          {(canManage || isPlatformAdmin) && !isCurrentUser && member.role !== 'owner' && (
                            <>
                              {/* Mute/Unmute button */}
                              {member.is_muted ? (
                                <button
                                  onClick={() => handleUnmute(member)}
                                  disabled={isUnmuting === `${member.user_type}:${member.user_id}`}
                                  className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                                  title="Unmute user"
                                >
                                  {isUnmuting === `${member.user_type}:${member.user_id}` ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                    </svg>
                                  )}
                                </button>
                              ) : (
                                <button
                                  onClick={() => openMuteModal(member)}
                                  className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                                  title="Mute user"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                  </svg>
                                </button>
                              )}
                              {/* Ban button */}
                              <button
                                onClick={() => openBanModal(member)}
                                className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                                title="Ban user"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              </button>
                              {/* Remove button */}
                              <button
                                onClick={() => handleRemoveMember(member)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                title="Remove member"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Banned Users Tab */}
          {activeTab === 'banned' && (isAdmin || isPlatformAdmin) && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">Banned Users</h3>
                <button
                  onClick={loadBannedUsers}
                  disabled={loadingBans}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {loadingBans ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {loadingBans ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : bannedUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <p>No banned users</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {bannedUsers.map(ban => (
                    <div key={`${ban.user_type}:${ban.user_id}`} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-medium">
                          {ban.display_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{ban.display_name}</div>
                          <div className="text-xs text-gray-500">
                            {ban.user_type} - Banned {ban.banned_at ? new Date(ban.banned_at).toLocaleDateString() : 'Unknown'}
                          </div>
                          {ban.ban_reason && (
                            <div className="text-xs text-orange-600 mt-1">
                              Reason: {ban.ban_reason}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnban(ban)}
                        disabled={isUnbanning === `${ban.user_type}:${ban.user_id}`}
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {isUnbanning === `${ban.user_type}:${ban.user_id}` ? 'Unbanning...' : 'Unban'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && isOwner && (
            <div className="space-y-4">
              <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                <h3 className="text-lg font-medium text-red-900">Archive Channel</h3>
                <p className="text-sm text-red-700 mt-1">
                  Archiving will hide this channel from all members. You can restore it later.
                </p>
                {showArchiveConfirm ? (
                  <div className="mt-4 flex items-center space-x-3">
                    <button
                      onClick={handleArchive}
                      disabled={isArchiving}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {isArchiving ? 'Archiving...' : 'Yes, Archive'}
                    </button>
                    <button
                      onClick={() => setShowArchiveConfirm(false)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowArchiveConfirm(true)}
                    className="mt-4 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Archive Channel
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Mute Modal */}
        {showMuteModal && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b bg-yellow-50">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Mute User</h2>
                </div>
                <button onClick={() => setShowMuteModal(false)} className="p-1 hover:bg-yellow-100 rounded">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {selectedMember.display_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="font-medium">{selectedMember.display_name}</div>
                    <div className="text-sm text-gray-500">{selectedMember.user_type}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mute Duration</label>
                  <select
                    value={muteDuration}
                    onChange={(e) => setMuteDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  >
                    <option value="1hour">1 Hour</option>
                    <option value="6hours">6 Hours</option>
                    <option value="1day">1 Day</option>
                    <option value="3days">3 Days</option>
                    <option value="7days">7 Days</option>
                    <option value="30days">30 Days</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                  <textarea
                    value={muteReason}
                    onChange={(e) => setMuteReason(e.target.value)}
                    placeholder="Why is this user being muted?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Muted users can still read messages but cannot send new messages in this channel.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
                <button
                  onClick={() => setShowMuteModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMute}
                  disabled={isMuting}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isMuting && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  )}
                  <span>{isMuting ? 'Muting...' : 'Mute User'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ban Modal */}
        {showBanModal && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b bg-orange-50">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Ban User</h2>
                </div>
                <button onClick={() => setShowBanModal(false)} className="p-1 hover:bg-orange-100 rounded">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                    {selectedMember.display_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="font-medium">{selectedMember.display_name}</div>
                    <div className="text-sm text-gray-500">{selectedMember.user_type}</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                  <textarea
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Why is this user being banned?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> Banned users will be immediately removed from the channel and cannot rejoin. This action should be used for serious violations.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
                <button
                  onClick={() => setShowBanModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBan}
                  disabled={isBanning}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {isBanning && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  )}
                  <span>{isBanning ? 'Banning...' : 'Ban User'}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// ARCHIVED CHANNELS MODAL
// ============================================
export const ArchivedChannelsModal = ({ isOpen, onClose }) => {
  const { getArchivedChannels, restoreChannel } = useChat();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadChannels();
    }
  }, [isOpen]);

  const loadChannels = async () => {
    setLoading(true);
    const data = await getArchivedChannels();
    setChannels(data);
    setLoading(false);
  };

  const handleRestore = async (channelId) => {
    setRestoringId(channelId);
    try {
      await restoreChannel(channelId);
      setChannels(prev => prev.filter(ch => ch.id !== channelId));
    } catch (err) {
      alert(err.message);
    }
    setRestoringId(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Archived Channels</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : channels.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              <p>No archived channels</p>
            </div>
          ) : (
            <div className="space-y-3">
              {channels.map(channel => (
                <div
                  key={channel.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <span className="font-medium text-gray-900">{channel.display_name}</span>
                    {channel.archived_at && (
                      <p className="text-xs text-gray-500">
                        Archived {new Date(channel.archived_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRestore(channel.id)}
                    disabled={restoringId === channel.id}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {restoringId === channel.id ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default {
  CreateChannelModal,
  BrowseChannelsModal,
  ChannelSettingsModal,
  ArchivedChannelsModal
};
